import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * PinTargetMenu — popover offering one or more pin destinations.
 *
 * Drives every case where a single item can be pinned to multiple
 * surfaces (homepage feed, parent collection, nav tab, ...). Each
 * option toggles independently; a checkmark indicates the item is
 * currently pinned to that surface.
 *
 * Used by:
 *   - per-card block toolbar (home + parent-collection)
 *   - block-detail modal toolbar (home + parent-collection)
 *   - CollectionBlockView card overlay (parent-group + nav-tab + home)
 *
 * StackBlockView intentionally does NOT use this menu: a Stack has
 * exactly one pin destination (its parent Collection), so the pin
 * button toggles that pin directly with no intermediate dropdown.
 *
 * Anchors absolutely to the nearest positioned ancestor in the same
 * style as `AddToCollectionPopover` — wrap the trigger in an anchor
 * `<div>` with `position: relative` at the call site.
 */
import React, { memo, useCallback, useEffect, useRef } from "react";
import { showToast } from "@shared/client/components/toast/show-toast";
import { FaIcon } from "@shared/client/components/fa-icon";
import { faCheck, faThumbtack } from "@fortawesome/pro-solid-svg-icons";
import { MAX_PINS_PER_SURFACE, } from "@shared/types/pin";
import { togglePinWithSync, usePinList } from "@/state/pin-store";
import "./pin-target-menu.scss";
export const PinTargetMenu = memo(({ item, targets, onClose, }) => {
    const ref = useRef(null);
    useEffect(() => {
        function handleClickOutside(e) {
            if (ref.current && !ref.current.contains(e.target)) {
                onClose();
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [onClose]);
    useEffect(() => {
        function handleKey(e) {
            if (e.key === "Escape")
                onClose();
        }
        document.addEventListener("keydown", handleKey);
        return () => document.removeEventListener("keydown", handleKey);
    }, [onClose]);
    return (_jsx("div", { ref: ref, className: "pin-target-menu", role: "menu", "aria-label": "Pin to...", onMouseDown: (e) => e.stopPropagation(), children: targets.map((target) => (_jsx(PinTargetMenuOption, { target: target, item: item, onClose: onClose }, target.key))) }));
});
const PinTargetMenuOption = memo(({ target, item, onClose, }) => {
    const pins = usePinList(target.scope, target.surface);
    const isPinned = pins.some((p) => p.type === item.type && p.id === item.id);
    const handleClick = useCallback(() => {
        void togglePinWithSync(target.scope, target.surface, item).then((result) => {
            if (result.ok === false && result.reason === "max") {
                showToast.warning(`You cannot pin more than ${MAX_PINS_PER_SURFACE} items here`);
            }
        });
        onClose();
    }, [target.scope, target.surface, item, onClose]);
    return (_jsxs("button", { type: "button", role: "menuitem", className: `pin-target-menu__item ${isPinned ? "pin-target-menu__item--checked" : ""}`, onClick: handleClick, children: [_jsx(FaIcon, { icon: faThumbtack, className: "pin-target-menu__item-icon" }), _jsx("span", { className: "pin-target-menu__item-label", children: target.label }), isPinned ? (_jsx(FaIcon, { icon: faCheck, className: "pin-target-menu__item-check", "aria-hidden": "true" })) : undefined] }));
});
// ---------------------------------------------------------------------------
// Helpers — common target compositions
// ---------------------------------------------------------------------------
/**
 * Standard pin targets for a Block sitting inside a Collection.
 *   - "Pin to home" — always
 *   - "Pin to {parent.title}" — only when parent isn't the user's Primary
 */
export function blockTargets(parent) {
    const targets = [
        {
            key: "home",
            label: "Pin to home",
            scope: { type: "account" },
            surface: "feed",
        },
    ];
    if (parent) {
        targets.push({
            key: `collection:${parent.id}`,
            label: `Pin to ${parent.title}`,
            scope: { type: "collection", id: parent.id },
            surface: "default",
        });
    }
    return targets;
}
/**
 * Standard pin targets for a layer-1 Collection.
 *   - "Pin to {parent.title}" — when parent is a non-Primary Group
 *   - "Pin as nav tab" — always available (account-scope tab pins)
 *   - "Pin to home" — always available (account-scope feed pins)
 */
export function collectionTargets(parent) {
    const targets = [];
    if (parent) {
        targets.push({
            key: `collection:${parent.id}`,
            label: `Pin to ${parent.title}`,
            scope: { type: "collection", id: parent.id },
            surface: "default",
        });
    }
    targets.push({
        key: "nav-tab",
        label: "Pin as nav tab",
        scope: { type: "account" },
        surface: "tab",
    });
    targets.push({
        key: "home",
        label: "Pin to home",
        scope: { type: "account" },
        surface: "feed",
    });
    return targets;
}
